import React, { useContext, useMemo, useCallback } from 'react'
import { Select, MenuItem, InputLabel, FormControl, TextField, Autocomplete, IconButton, Divider, InputAdornment } from '@mui/material'
import { Star, StarOutline } from '@mui/icons-material'
import { LoadingContext } from '../M_Utils'
import { useTranslation } from 'react-i18next'
import { useFavorites, useRegisterFavorite } from '../M_FormFavorites/FavoritesContext'

export function M_Select({ label, value, options, color, secondColor,
    disabled, required, onChange, onClick, multiple, minWidth,
    itemTemplate, height, margin, padding, fontSize, marginLeft,
    marginRight, size, autocomplete, onChangeValue, hasNone, translate, disabledColor,
    onSaveFavorite, onRemoveFavorite, fieldFavorites,
    favoritesFieldName, favoritesGroupName, favoritesIdKey, favoritesVarKey }) {

    const ctx = useContext(LoadingContext)
    const { t } = useTranslation()
    const favCtx = useFavorites()
    const fieldId = label
    ? `field-${(translate ? t(label) : label).toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`
    : 'field-unlabelled'

    // Registo automatico no sistema de favoritos
    useRegisterFavorite({ label, favoritesFieldName, favoritesGroupName, favoritesIdKey, favoritesVarKey, required })

    color = color ? color : ctx.primaryColor ? ctx.primaryColor : 'var(--color-black)'
    secondColor = secondColor ? secondColor : ctx.secondaryColor ? ctx.secondaryColor : 'var(--color-black)'

    // Se favoritesFieldName e dado, usa o contexto automaticamente
    const ctxFieldFavorites = favoritesFieldName && favCtx ? favCtx.getFieldFavoriteValues(favoritesFieldName) : null
    const ctxSaveFavorite = useCallback((option) => {
        if (!favCtx || !favoritesFieldName) return
        const val = option?.value !== undefined ? option.value : option
        const lbl = option?.label || ''
        const idVal = favoritesIdKey ? (option?.value || val) : null
        favCtx.saveFieldFavoriteAuto(favoritesFieldName, val, lbl, favoritesGroupName || null, favoritesIdKey || null, idVal, favoritesVarKey || favoritesFieldName)
    }, [favCtx, favoritesFieldName, favoritesGroupName, favoritesIdKey, favoritesVarKey])
    const ctxRemoveFavorite = useCallback((val) => {
        if (!favCtx || !favoritesFieldName) return
        favCtx.removeFieldFavorite(favoritesFieldName, val)
    }, [favCtx, favoritesFieldName])

    // Props diretas tem prioridade sobre contexto
    const effectiveSaveFavorite = onSaveFavorite || (favoritesFieldName && favCtx ? ctxSaveFavorite : null)
    const effectiveRemoveFavorite = onRemoveFavorite || (favoritesFieldName && favCtx ? ctxRemoveFavorite : null)
    const effectiveFieldFavorites = fieldFavorites || ctxFieldFavorites || []

    const hasFavorites = !!effectiveSaveFavorite
    const favoriteValues = effectiveFieldFavorites

    // Verifica se o valor atual e favorito
    const isCurrentValueFavorite = useMemo(() => {
        if (!hasFavorites || !value) return false
        const currentVal = autocomplete ? value?.value : value
        return favoriteValues.some(fv => String(fv).toLowerCase() === String(currentVal).toLowerCase())
    }, [hasFavorites, value, favoriteValues, autocomplete])

    const handleStarClick = (e) => {
        e.stopPropagation()
        if (!value || (autocomplete && !value?.value)) return
        if (isCurrentValueFavorite) {
            if (effectiveRemoveFavorite) {
                const val = autocomplete ? value.value : value
                effectiveRemoveFavorite(val)
            }
        } else {
            if (effectiveSaveFavorite) {
                effectiveSaveFavorite(autocomplete ? value : { label: '', value })
            }
        }
    }

    // Ordena opcoes: favoritos primeiro
    const sortedOptions = useMemo(() => {
        if (!hasFavorites || !options || favoriteValues.length === 0) return options
        const translated = translate ? options.map(el => ({ ...el, label: t(el.label) })) : options
        const favs = translated.filter(o => favoriteValues.some(fv => String(fv).toLowerCase() === String(o.value).toLowerCase()))
        const rest = translated.filter(o => !favoriteValues.some(fv => String(fv).toLowerCase() === String(o.value).toLowerCase()))
        if (favs.length === 0) return translated
        return [
            ...favs.map((o, i) => ({ ...o, _isFavorite: true, ...(i === 0 ? { _firstFavorite: true } : {}) })),
            ...(rest.length > 0 ? [{ ...rest[0], _dividerBefore: true }, ...rest.slice(1)] : [])
        ]
    }, [options, favoriteValues, hasFavorites, translate, t])

    const starButton = hasFavorites ? (
        <IconButton
            className="fav-star-btn"
            size="small"
            onClick={handleStarClick}
            onMouseDown={(e) => { e.stopPropagation(); e.preventDefault() }}
            sx={{ padding: '2px' }}
            disabled={!value || (autocomplete && !value?.value)}
        >
            {isCurrentValueFavorite ?
                <Star sx={{ color: 'var(--color-yellow, #f9a825)', fontSize: '20px' }} /> :
                <StarOutline sx={{ color: '#bbb', fontSize: '20px' }} />
            }
        </IconButton>
    ) : null

    const styles = {
        margin: margin,
        width: size ? size : null,
        marginLeft: marginLeft,
        marginRight: marginRight,
        '& .fav-star-btn': { opacity: 0, transition: 'opacity 0.15s' },
        '&:hover .fav-star-btn': { opacity: 1 },
        '& label': { color: secondColor ? secondColor : 'var(--color-black)', },
        '& label.Mui-focused': { color: secondColor ? secondColor : 'var(--color-black)' },
        '& .MuiInput-underline:after': { borderBottomColor: color ? color : 'var(--color-black)' },
        "& .MuiInputBase-root.Mui-disabled": {
            "& > fieldset": {
                borderColor: disabledColor ? disabledColor : 'rgba(0, 0, 0, 0.38) !important'
            }
        },
        '& .MuiOutlinedInput-input': {
            backgroundColor: 'var(--color-white)',
            padding: padding,
        },
        '& .MuiOutlinedInput-root': {
            '& fieldset': {
                borderColor: color ? color : 'var(--color-black)',
                width: size ? size : 'auto',
                height: height,
            },
            '&:hover fieldset': {
                borderColor: color ? color : 'var(--color-black)',
            },
            '&.Mui-focused fieldset': {
                borderColor: color ? color : 'var(--color-black)',
                boxShadow: `2px 2px 2px ${color}`
            },
            "& legend": {
                letterSpacing: -1.9,
            }
        },
    }

    return (
        <>{autocomplete ?
            <Autocomplete
                id="combo-box-demo"
                options={hasFavorites ? sortedOptions : (translate && options.map(element => { return { ...element, label: t(element.label) } }) || options)}
                sx={styles}
                getOptionLabel={(e) => {
                    return e?.label || ''
                }}
                getOptionKey={(e) => {
                    return e.Id
                }}
                renderOption={hasFavorites && favoriteValues.length > 0 ? (props, option) => {
                    const { key, ...restProps } = props
                    return (
                        <div key={key}>
                            {option._firstFavorite && <li style={{ padding: '4px 16px', fontSize: '12px', fontWeight: 600, color: 'var(--color-black)', pointerEvents: 'none' }}>{t('Favorites')}</li>}
                            {option._dividerBefore && <>
                                <Divider sx={{ my: '4px' }} />
                                <li style={{ padding: '4px 16px', fontSize: '12px', fontWeight: 600, color: 'var(--color-black)', pointerEvents: 'none' }}>{t('All')}</li>
                            </>}
                            <li {...restProps}>
                                {option._isFavorite && <Star sx={{ color: 'var(--color-yellow, #f9a825)', fontSize: '16px', marginRight: '6px' }} />}
                                {option.label}
                            </li>
                        </div>
                    )
                } : undefined}
                isOptionEqualToValue={(option, value) => 
                    option?.value === value?.value || option?.value === value
                }
                multiple={multiple}
                value={value}
                onChange={(event, value, reason) => onChangeValue(value)}
                renderInput={(params) => <TextField
                    {...params}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {starButton}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                    margin="dense"
                    required={required}
                    label={translate && t(label) || label}
                    variant="outlined"
                    style={{ height: height, textOverflow: 'ellipsis' }}
                    sx={styles}
                    size="small"
                    id={fieldId}
                />}
            />
            : <FormControl
                sx={{
                    margin: margin,
                    marginLeft: marginLeft,
                    marginRight: marginRight,
                    '& .fav-star-btn': { opacity: 0, transition: 'opacity 0.15s' },
                    '&:hover .fav-star-btn': { opacity: 1 },
                    '& label': { color: disabled ? 'rgba(0, 0, 0, 0.38)' : secondColor ? secondColor : 'var(--color-black)' },
                    '& label.Mui-focused': { color: secondColor ? secondColor : 'var(--color-black)' },
                    '& .MuiInput-underline:after': { color: color ? color : 'var(--color-black)', borderColor: "transparent" },
                    "& .MuiInputBase-root.Mui-disabled": {
                        "& > fieldset": {

                            borderColor: 'rgba(0, 0, 0, 0.38) !important'
                        },
                    },
                    '& .MuiOutlinedInput-root': {
                        height: height,
                        fontSize: fontSize,
                        padding: padding,
                        '& fieldset': {
                            borderColor: color ? color : 'var(--color-black)',
                        },
                        '&:hover fieldset': {
                            borderColor: color ? color : 'var(--color-black)',
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: color ? color : 'var(--color-black)',
                            boxShadow: `2px 2px 2px ${color}`
                        },
                    },
                    minWidth: minWidth,
                }}
                margin="dense"
                fullWidth
                size='small'
                required={required}
                id={fieldId}
            >
                <InputLabel shrink>{translate && t(label) || label}</InputLabel>
                <Select
                    label={translate && t(label) || label}
                    notched={true}
                    value={value ?? ''}
                    disabled={disabled}
                    required={required}
                    onChange={onChange}
                    multiple={multiple}
                    endAdornment={starButton ? (
                        <InputAdornment position="end" sx={{ marginRight: '8px' }}>
                            {starButton}
                        </InputAdornment>
                    ) : undefined}
                    MenuProps={{
                        PaperProps: {
                            sx: {
                                maxHeight: 200,
                                "& .Mui-selected": {
                                    backgroundColor: "var(--color-grey-light)",
                                    height: height
                                },
                                "& .Mui-selected:hover": {
                                    backgroundColor: "var(--color-grey-light)"
                                },
                            }
                        }
                    }}
                >
                    {!hasNone && <MenuItem value="" onClick={() => onClick ? onClick({}) : {}}  > <em>None</em> </MenuItem>}
                    {hasFavorites && favoriteValues.length > 0 && (
                        sortedOptions.filter(o => o._isFavorite).map((el, index) => (
                            <MenuItem key={`fav-${index}`} disableRipple={true} value={el.value} onClick={() => onClick ? onClick(el) : {}} >
                                <Star sx={{ color: 'var(--color-yellow, #f9a825)', fontSize: '16px', marginRight: '6px' }} />
                                {itemTemplate ? itemTemplate(el) : el.label}
                            </MenuItem>
                        ))
                    )}
                    {hasFavorites && favoriteValues.length > 0 && <Divider />}
                    {(hasFavorites ? sortedOptions.filter(o => !o._isFavorite) : options).map((el, index) => (
                        <MenuItem key={index} disableRipple={true} value={el.value} onClick={() => onClick ? onClick(el) : {}}  > {itemTemplate ? itemTemplate(el) : el.label} </MenuItem>
                    ))}
                </Select>
            </FormControl>}
        </>
    )
}
